/**
 * TEMAS SALVOS DO STUDIO — modelos visuais prontos que ficam NO SISTEMA e
 * NÃO são aplicados sozinhos (decisão do usuário, 2026-09-26: "deixar esse
 * tema dentro do sistema, mas não aplicado; eu quero poder fazer isso
 * manualmente").
 *
 * Aplicar um tema é escrever no RASCUNHO do Studio, pelo mesmo caminho de
 * qualquer edição: a pessoa vê em VISUALIZAR e só então PUBLICA. Nenhum tema
 * muda o sistema ativo por conta própria, e voltar é aplicar outro tema (ou
 * restaurar uma versão no Histórico).
 *
 * Os arquivos (logos) ficam em `public/studio/presets/<tema>/` e, ao aplicar,
 * entram no armazenamento oficial como qualquer arquivo enviado — com a mesma
 * validação de tipo, conteúdo e tamanho.
 */
import { DEFAULT_LOGIN, defaultVisualDesign, type StudioAsset, type StudioSectionId, type VisualDesign } from "./visual-design.js";

export type PresetAssetKey = "documentLogo" | "loginLogo" | "loginBrandLogo";

export interface PresetAsset {
  readonly key: PresetAssetKey;
  /** Caminho público do arquivo (servido pelo próprio sistema). */
  readonly url: string;
  readonly fileName: string;
  readonly label: string;
}

export interface VisualPreset {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  /** O que o tema reescreve no rascunho — o resto do rascunho fica como está. */
  readonly sections: readonly StudioSectionId[];
  readonly assets: readonly PresetAsset[];
  /** Imagem de referência do modelo aprovado (só para mostrar no Studio). */
  readonly referenceImage: string | null;
  readonly highlights: readonly string[];
  apply(draft: VisualDesign, assets: Partial<Record<PresetAssetKey, StudioAsset>>): VisualDesign;
}

const EMTHOS_DIR = "/studio/presets/emthos";

export const VISUAL_PRESETS: readonly VisualPreset[] = [
  {
    id: "INSPECT_IA",
    name: "INSPECT IA (padrão)",
    description: "A identidade de fábrica: tema escuro técnico, login escuro com ambiente industrial e INSPECT IA no cabeçalho dos relatórios.",
    sections: ["identity", "logos", "theme", "typography", "login", "layout", "documents", "templates", "signature", "dashboard"],
    assets: [],
    referenceImage: null,
    highlights: ["Tema escuro INSPECT IA", "Login escuro técnico", "Relatórios com o texto INSPECT IA"],
    apply: () => defaultVisualDesign()
  },
  {
    id: "EMTHOS",
    name: "EMTHOS",
    description: "Login claro industrial (cinza, vermelho e grafite) com as marcas EMTHOS e INSPECT IA, e o logo EMTHOS no cabeçalho dos relatórios.",
    sections: ["login", "logos", "documents"],
    assets: [
      { key: "documentLogo", url: `${EMTHOS_DIR}/emthos-logo.png`, fileName: "emthos-logo.png", label: "Logo documental" },
      { key: "loginBrandLogo", url: `${EMTHOS_DIR}/emthos-logo.png`, fileName: "emthos-logo.png", label: "Logo da marca no login" },
      { key: "loginLogo", url: `${EMTHOS_DIR}/emthos-gear.png`, fileName: "emthos-gear.png", label: "Ícone do card de acesso" }
    ],
    referenceImage: `${EMTHOS_DIR}/emthos-login-modelo.jpg`,
    highlights: ["Login claro: fundo cinza, vermelho e grafite", "Marca EMTHOS + INSPECT IA no login", "\"Plataforma licenciada pela EMTHOS\"", "Logo EMTHOS nos relatórios"],
    apply: (draft, assets) => ({
      ...draft,
      logos: { ...draft.logos, document: assets.documentLogo ?? draft.logos.document, login: assets.loginLogo ?? draft.logos.login },
      documents: { ...draft.documents, logoMode: "IMAGEM" },
      login: {
        ...DEFAULT_LOGIN,
        variant: "CLARO",
        brandLogo: assets.loginBrandLogo ?? null,
        backgroundImage: null,
        backgroundColor: "#E4E6E8",
        accentColor: "#D0111A",
        buttonColor: "#D0111A",
        slogan: "TECNOLOGIA A FAVOR\nDA CONFIABILIDADE",
        cardSubtitle: "Plataforma licenciada pela *EMTHOS*",
        footerText: "PLATAFORMA LICENCIADA PELA *EMTHOS*"
      }
    })
  }
];

export function presetById(id: string): VisualPreset | null {
  return VISUAL_PRESETS.find(p => p.id === id) ?? null;
}
